import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Star, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { useWatchlist, WatchEntry } from "@/components/WatchButton";

export const WatchlistPanel = () => {
  const { data, isLoading } = useWatchlist();
  const queryClient = useQueryClient();
  const list: WatchEntry[] = data?.watchlist || [];

  const update = async (entry: WatchEntry, patch: Partial<WatchEntry>) => {
    const next = { ...entry, ...patch };
    if (!next.news && !next.price) { toast.error("Keep at least one alert type — or remove the coin"); return; }
    try {
      await api.post("/account/watchlist", next);
      queryClient.invalidateQueries({ queryKey: ["watchlist"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (symbol: string) => {
    try {
      await api.delete(`/account/watchlist/${symbol}`);
      queryClient.invalidateQueries({ queryKey: ["watchlist"] });
      toast.success(`${symbol} removed`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (isLoading) return <p className="text-gray-500 text-sm">Loading watchlist…</p>;

  if (!list.length) {
    return (
      <div className="text-center py-14 border border-dashed border-white/10 rounded-xl" data-testid="watchlist-empty">
        <Star className="h-7 w-7 text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400">No coins on your watchlist yet.</p>
        <p className="text-gray-500 text-sm mt-1">
          Open a coin page like <Link to="/coin/BTC" className="text-[#00E5FF] hover:underline">Bitcoin</Link> and
          tap <span className="text-white font-semibold">Watch</span> to get news and price alerts.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="watchlist-panel">
      {list.map((w) => (
        <div key={w.symbol} data-testid={`watchlist-row-${w.symbol}`}
          className="flex flex-wrap items-center gap-x-5 gap-y-2 bg-[#121212] border border-white/10 rounded-lg px-4 py-3">
          <Link to={`/coin/${w.symbol}`} className="font-mono font-bold text-white text-base hover:text-[#00E5FF] transition-colors w-16">
            {w.symbol}
          </Link>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <Switch checked={!!w.news} onCheckedChange={(v: any) => update(w, { news: !!v })} />
            News
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <Switch checked={!!w.price} onCheckedChange={(v: any) => update(w, { price: !!v })} />
            Price ±5%
          </label>
          <button onClick={() => remove(w.symbol)} data-testid={`watchlist-remove-${w.symbol}`}
            className="ml-auto text-gray-500 hover:text-red-400 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <p className="text-[12px] text-gray-500">
        Alerts arrive as push notifications — make sure they're enabled via the bell icon in the header.
      </p>
    </div>
  );
};
